// native window(popup) 로 편집기를 열 때, 이미 구성된 SendHelper 인스턴스를
// opener → popup 으로 넘기기 위한 레지스트리.
//
// 왜 postMessage 가 아니라 객체 핸드오프인가:
// - reply/forward 는 prevEmail(EmailObj: 본문 HTML·인용문·첨부/inline blob 참조)을 포함한
//   SendHelper 를 필요로 한다. 이는 직렬화가 어렵고(blob URL·함수·순환참조), 직렬화 비용도 크다.
// - 기존 openSendMailWindow(iframe 방식)도 동일하게 sendHelper 를 window global 로 넘겨 왔다.
// - 같은 origin + opener 관계이므로 popup 에서 opener 의 객체 참조에 접근할 수 있다.
//
// popup 은 handoff-only 로 동작한다(자체 계정/데이터 부팅 없음). winId 로 이 레지스트리에서
// sendHelper 를 소비하지 못하면(팝업 차단/단일창 재사용/새로고침) 항목이 남아 SendHelper+prevEmail
// (+첨부/blob)을 GC 하지 못하므로, TTL 타임아웃으로 정리한다.

import type SendHelper from 'pages/main/components/SendHelper'

export const POPUP_WIN_ID_PARAM = 'winId'

// popup 이 winId 를 소비하지 않을 때 등록 항목을 정리하기까지의 유예 시간.
const UNCONSUMED_HANDOFF_TTL_MS = 60_000

export interface PopupHandoffEntry {
  sendHelper: SendHelper
  title?: string
  // 미소비 항목 정리 타이머(opener realm 의 setTimeout id). 소비/차단 시 해제한다.
  cleanupTimer?: number
}

interface HandoffHostWindow extends Window {
  __omPopupSendHelpers?: Record<string, PopupHandoffEntry>
}

// opener 측: sendHelper 를 등록하고 popup URL 에 실어 보낼 winId 를 반환한다.
export function registerPopupSendHelper(sendHelper: SendHelper, title?: string): string {
  const host = window as HandoffHostWindow
  if (host.__omPopupSendHelpers == null) {
    host.__omPopupSendHelpers = {}
  }
  const winId = `popup-${Date.now()}-${Math.floor(Math.random() * 1e6)}`

  // 미소비 항목 누수 방지: TTL 경과 시 아직 남아 있으면 제거한다(소비 시 clearTimeout 으로 해제).
  const cleanupTimer = window.setTimeout(() => {
    const registry = host.__omPopupSendHelpers
    if (registry != null && registry[winId] != null) {
      delete registry[winId]
    }
  }, UNCONSUMED_HANDOFF_TTL_MS)

  host.__omPopupSendHelpers[winId] = { sendHelper, title, cleanupTimer }
  return winId
}

// opener 측: 등록 항목을 즉시 제거한다(팝업 차단 등으로 popup 이 소비하지 못할 때).
export function unregisterPopupSendHelper(winId: string): void {
  const host = window as HandoffHostWindow
  const registry = host.__omPopupSendHelpers
  const entry = registry?.[winId]
  if (entry == null) {
    return
  }
  if (entry.cleanupTimer != null) {
    window.clearTimeout(entry.cleanupTimer)
  }
  delete registry[winId]
}

// popup 측: opener 레지스트리에서 sendHelper 를 꺼내고 등록을 제거한다(1회성).
export function consumePopupSendHelper(winId: string): PopupHandoffEntry | null {
  const opener = window.opener as HandoffHostWindow | null
  if (opener == null || opener.closed === true) {
    return null
  }
  const registry = opener.__omPopupSendHelpers
  const entry = registry?.[winId]
  if (entry == null) {
    return null
  }
  if (entry.cleanupTimer != null) {
    // 타이머는 opener realm 의 setTimeout 으로 등록됐으므로 opener realm 에서 해제해야 한다.
    opener.clearTimeout(entry.cleanupTimer)
  }
  delete registry[winId]
  return entry
}
